import React from "react";
import { Avatar, Box, Button, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import { useHistory } from "react-router-dom";
import { usePatientContext } from "../../context/PatientContext";
import { patientAgeYears, toLegacyPatient } from "../../context/normalizePatient";
import { displayPatientNameFromParts } from "../../utils/displayPatientName";

const ACTION_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.82rem",
  borderRadius: "10px",
  borderColor: "#b9dcd6",
  color: "#0E3F66",
  backgroundColor: "#fff",
  whiteSpace: "nowrap",
  "&:hover": { backgroundColor: "#f2fbf9", borderColor: "#0E3F66" },
};

/**
 * Slim strip under the navbar showing the patient the whole app is working on.
 * Visible only when a patient is active.
 *
 * Consultation is intentionally gated on a resolved encounter: an appointment
 * must be known before anything can be written against it.
 */
export default function ActivePatientBar() {
  const history = useHistory();
  const { patient, encounter, hasPatient, hasEncounter, clearPatient } =
    usePatientContext();
  const isDoctor = sessionStorage.getItem("roleType") === "doctor";

  if (!hasPatient) return null;

  const name = displayPatientNameFromParts(patient, "Patient");
  const age = patientAgeYears(patient);

  const meta = [
    patient.gender,
    age !== "" ? `${age}y` : "",
    patient.patient_id,
    patient.phone_no,
  ].filter(Boolean);

  const goToAppointments = () => {
    history.push({
      pathname: "/doctor/appointments",
      state: { preselectedAppointmentPatient: toLegacyPatient(patient) },
    });
  };

  const goToInvoice = () => {
    history.push({
      pathname: "/doctor/invoice",
      state: { preselectedInvoicePatient: toLegacyPatient(patient) },
    });
  };

  const goToRecords = () => history.push("/patient-local-data");

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1100,
        backgroundColor: "#e7f6f3",
        borderBottom: "1px solid #c9e7e1",
        px: { xs: 1.5, md: 3 },
        py: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
        <Avatar sx={{ bgcolor: "#0f9b8e", width: 40, height: 40 }}>
          <PersonIcon />
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ fontWeight: 700, color: "#0b2b45", fontSize: "1rem" }}>
              {name}
            </Typography>
            <Typography sx={{ color: "#496a80", fontSize: "0.85rem" }}>
              {meta.join(" · ")}
            </Typography>
          </Box>
          {hasEncounter ? (
            <Chip
              size="small"
              label={[
                encounter?.doctorName,
                encounter?.department,
                encounter?.startTime,
              ]
                .filter(Boolean)
                .join(" · ")}
              sx={{ mt: 0.25, backgroundColor: "#0f9b8e", color: "#fff", fontSize: "0.72rem" }}
            />
          ) : (
            <Typography sx={{ color: "#5c7a8c", fontSize: "0.78rem" }}>
              Active patient — carried across every module
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          onClick={goToAppointments}
          startIcon={<EventAvailableOutlinedIcon />}
          sx={ACTION_SX}
        >
          Appts
        </Button>

        <Tooltip
          title={isDoctor ? "Invoicing is not available for doctors" : ""}
        >
          <span>
            <Button
              variant="outlined"
              disabled={isDoctor}
              onClick={goToInvoice}
              startIcon={<ReceiptLongOutlinedIcon />}
              sx={ACTION_SX}
            >
              Invoice
            </Button>
          </span>
        </Tooltip>

        <Button
          variant="outlined"
          onClick={goToRecords}
          startIcon={<FolderSharedOutlinedIcon />}
          sx={ACTION_SX}
        >
          Records
        </Button>

        <Tooltip title="Clear active patient">
          <IconButton
            onClick={clearPatient}
            size="small"
            sx={{
              color: "#d32f2f",
              border: "1px solid #f3c9c9",
              backgroundColor: "#fff",
              "&:hover": { backgroundColor: "#fdeaea" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
